import { inject } from '@angular/core';
import { Grant, PermissionConfig } from '@xt/core/permissions';
import { Router } from '@angular/router';
import { UserNotificationService } from '@xt/core/user-notification';
import { WidgetPermission } from './widget-permission';
import { AppPermission } from './app-permission';
import { RequireUserRole } from '@core/grants/require-user-role';
import { RequireMfaStatus } from '@core/grants/require-mfa-status';
import { PatientRepresentationsPermissions } from '@xt/patient-representations-cdk';
import { PatientListPermissions } from '@xt/patient-list-cdk';

export const permissionConfig = new PermissionConfig()
  .onPermissionDenied(Grant.ALWAYS, () => {
    const router = inject(Router);
    const snackbar = inject(UserNotificationService);
    snackbar.error('app.errors.403');
    return router.createUrlTree(['/dashboard']);
  })

  .define({
    [AppPermission.Dashboard]: RequireMfaStatus.VERIFIED,
    [AppPermission.QuestionnaireRenderer]: RequireMfaStatus.VERIFIED,
    [AppPermission.Appointments]: RequireMfaStatus.VERIFIED.and(
      Grant.some(
        RequireUserRole.PRIVATE_RECORD,
        RequireUserRole.CUR_VERIFIED_PATIENT,
        RequireUserRole.CUR_VERIFIED_PROVIDER,
      ),
    ),
    [AppPermission.Messenger]: Grant.every(
      RequireMfaStatus.VERIFIED,
      Grant.some(
        RequireUserRole.CUR_VERIFIED_PATIENT,
        RequireUserRole.CUR_VERIFIED_PROVIDER,
        RequireUserRole.CUR_VERIFIED_REPRESENTATIVE,
      ),
    ),
    [AppPermission.PatientList]: RequireUserRole.CUR_VERIFIED_PROVIDER,
    [AppPermission.Materialpool]: RequireUserRole.CUR_VERIFIED_PROVIDER,
    [AppPermission.Representations]:
      RequireUserRole.CUR_VERIFIED_REPRESENTATIVE,

    [PatientRepresentationsPermissions.Read]: Grant.some(
      RequireUserRole.CUR_VERIFIED_PATIENT,
      RequireUserRole.CUR_VERIFIED_PROVIDER,
      RequireUserRole.CUR_VERIFIED_REPRESENTATIVE,
    ),
    [PatientRepresentationsPermissions.Delete]: Grant.some(
      RequireUserRole.CUR_VERIFIED_PATIENT,
      RequireUserRole.CUR_VERIFIED_REPRESENTATIVE,
    ),

    [PatientListPermissions.ChangeRepresentativePersonalData]:
      RequireUserRole.CUR_VERIFIED_REPRESENTATIVE,

    [AppPermission.Documents]: RequireMfaStatus.VERIFIED.and(
      RequireUserRole.PRIVATE_RECORD,
      RequireUserRole.CUR_VERIFIED_PATIENT,
    ),
    [AppPermission.Questionnaires]: RequireMfaStatus.VERIFIED.and(
      RequireUserRole.PRIVATE_RECORD,
      RequireUserRole.CUR_VERIFIED_PATIENT,
    ),
    [AppPermission.MedicalData]: RequireMfaStatus.VERIFIED.and(
      Grant.some(
        RequireUserRole.PRIVATE_RECORD,
        RequireUserRole.CUR_VERIFIED_PATIENT,
      ),
    ),
    [AppPermission.Diary]: RequireMfaStatus.VERIFIED.and(
      RequireUserRole.PRIVATE_RECORD,
      RequireUserRole.CUR_VERIFIED_PATIENT,
    ),
    [AppPermission.Notes]: RequireMfaStatus.VERIFIED.and(
      RequireUserRole.PRIVATE_RECORD,
      RequireUserRole.CUR_VERIFIED_PATIENT,
    ),
    [AppPermission.PrivacyCockpit]: RequireMfaStatus.VERIFIED.and(
      RequireUserRole.PRIVATE_RECORD,
      RequireUserRole.CUR_VERIFIED_PATIENT,
    ),
    [AppPermission.PrivacyCockpitSide]: RequireMfaStatus.VERIFIED.and(
      RequireUserRole.PRIVATE_RECORD,
      RequireUserRole.CUR_VERIFIED_PATIENT,
    ),
    [AppPermission.Account]: RequireMfaStatus.VERIFIED,
    [AppPermission.Identification]: RequireMfaStatus.VERIFIED.and(
      Grant.some(
        RequireUserRole.PRIVATE_RECORD,
        RequireUserRole.CUR_VERIFIED_PATIENT,
        RequireUserRole.CUR_VERIFIED_REPRESENTATIVE,
        RequireUserRole.CUR_VERIFIED_PROVIDER,
      ),
    ),
    [AppPermission.Verification]: RequireMfaStatus.VERIFIED.and(
      Grant.some(RequireUserRole.CUR_VERIFIED_PROVIDER),
    ),
    [AppPermission.Polavis]: RequireUserRole.CUR_VERIFIED_PROVIDER,
    [AppPermission.Emergency]: Grant.some(
      RequireUserRole.PRIVATE_RECORD,
      RequireUserRole.CUR_VERIFIED_PATIENT,
      RequireUserRole.CUR_VERIFIED_REPRESENTATIVE,
    ),

    [AppPermission.JumpingPoint]: RequireMfaStatus.VERIFIED.and(
      Grant.some(
        RequireUserRole.CUR_VERIFIED_PATIENT,
        RequireUserRole.CUR_VERIFIED_PROVIDER,
      ),
    ),

    [AppPermission.Reconciliation]: RequireMfaStatus.VERIFIED.and(
      RequireUserRole.CUR_VERIFIED_PROVIDER,
    ),
    [AppPermission.Logbook]: RequireMfaStatus.VERIFIED.and(
      Grant.some(
        RequireUserRole.PRIVATE_RECORD,
        RequireUserRole.CUR_VERIFIED_PATIENT,
        RequireUserRole.CUR_VERIFIED_PROVIDER,
      ),
    ),

    [AppPermission.Monitoring]: RequireMfaStatus.VERIFIED.and(
      RequireUserRole.DPO,
    ),
    [AppPermission.InactiveAccounts]: RequireMfaStatus.VERIFIED.and(
      RequireUserRole.DPO,
    ),
    [AppPermission.Documentation]: RequireMfaStatus.VERIFIED.and(
      RequireUserRole.DPO,
    ),

    /**
     * Widget Permissions
     */
    [WidgetPermission.Messenger]: Grant.alias(AppPermission.Messenger),
    [WidgetPermission.Documents]: Grant.alias(AppPermission.Documents),
    [WidgetPermission.Questionnaires]: Grant.alias(
      AppPermission.Questionnaires,
    ),
    [WidgetPermission.Appointments]: Grant.alias(AppPermission.Appointments),
    [WidgetPermission.Diary]: Grant.alias(AppPermission.Diary),
    [WidgetPermission.Notes]: Grant.alias(AppPermission.Notes),
    [WidgetPermission.JumpingPoint]: Grant.alias(AppPermission.JumpingPoint),
    [WidgetPermission.Patients]: Grant.alias(AppPermission.PatientList),
    [WidgetPermission.Materialpool]: Grant.alias(AppPermission.Materialpool),
    [WidgetPermission.Representations]: Grant.alias(
      AppPermission.Representations,
    ),
    [WidgetPermission.Monitoring]: Grant.alias(AppPermission.Monitoring),
    [WidgetPermission.PermissionAndPolicy]: RequireUserRole.DPO,
    [WidgetPermission.InactiveAccounts]: Grant.alias(
      AppPermission.InactiveAccounts,
    ),
    [WidgetPermission.Reports]: RequireUserRole.DPO,
    /*[WidgetPermission.PatientRecord]: Grant.alias(AppPermission.PatientRecord)*/
  });
